import React, { useState } from 'react'
import styled from 'styled-components'
import { OutboundLink } from 'gatsby-plugin-google-gtag'

import TabGroup from '../shared/TabGroup'
import { SmallP } from '../shared/text'
import { media } from '../../styles/util'

import BulletBlob from '../../assets/img/pdp/bullet-blob.svg'

const TabWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  ${media.mobile} {
    padding: 25px 16px 80px 16px;
  }
  ${media.tablet} {
    padding: 25px 80px 80px 80px;
  }
  ${media.desktop} {
    min-height: 500px;
    padding: 75px 40px 95px 40px;
  }

  & h1 {
    max-width: 776px;
    margin: 0 auto;
    text-align: center;
  }
`

const BulletGrid = styled.div`
  width: 100%;
  max-width: 864px;
  margin: 0 auto;
  display: grid;
  grid-row-gap: 38px;
  margin-top: 40px;
  text-align: left;

  ${media.mobile} {
    grid-row-gap: 20px;
  }
  ${media.desktop} {
    grid-row-gap: 38px;
  }
`

const BulletGroup = styled.div`
  display: grid;
  grid-template-columns: 20px 1fr;
  grid-column-gap: 11px;
  grid-row-gap: 17px;

  & a:hover {
    text-decoration: none;
    color: ${props => props.theme.colors.blue};

    & svg path {
      fill: ${props => props.theme.colors.blue};
    }
  }
`

const BulletIcon = styled.img`
  margin-top: 3px;
`

const StudyTitle = styled.h3`
  font-size: 16px;
  line-height: 20px;
  font-family: Beacon, sans-serif;

  & svg {
    height: 16px;
    width: 51px;
    margin-bottom: -3px;
  }

  ${media.tablet} {
    font-size: 18px;
    line-height: 24px;

    & svg {
      height: 18px;
      width: 59px;
      margin-bottom: -4px;
    }
  }
`

const StudyCitation = styled(SmallP)`
  ${media.mobile} {
    display: none;
  }
  ${media.tablet} {
    display: block;
    color: ${props => props.theme.colors.lightPurple};
    grid-column-start: 2;
    max-width: 700px;
  }
`

const HeaderTitle = styled.h1`
  font-size: 25px;
  line-height: 28px;
  ${media.tablet} {
    font-size: 30px;
    line-height: 40px;
  }
`

interface SectionInterface {
  label: string
  header: string
  bullets: { title: string; citation: string; link: string }[]
}

const Research = ({ research }: { research: SectionInterface[] }) => {
  const [activeTabIdx, setActiveTabIdx] = useState(0)

  return (
    <TabGroup
      className="pdp__research-tab"
      tabs={research}
      activeTabIdx={activeTabIdx}
      setActiveTabIdx={setActiveTabIdx}
    >
      <TabWrapper>
        <HeaderTitle>{research[activeTabIdx].header}</HeaderTitle>
        <BulletGrid>
          {research[activeTabIdx].bullets.map(bullet => {
            const displayTitle = bullet.title.replace(
              'anxiety',
              ` <svg
                width="59"
                height="18"
                viewBox="0 0 59 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.546 14.144C4.842 14.144 5.778 13.532 6.156 12.776H6.21V12.83C6.21 13.352 6.336 13.712 6.606 14H8.226V13.91C7.83 13.586 7.65 12.884 7.65 12.02V7.97C7.65 6.08 6.516 4.856 4.374 4.856C2.304 4.856 1.044 6.044 0.702 7.97L2.232 8.222V8.132C2.376 6.872 3.06 6.17 4.356 6.17C5.598 6.17 6.138 6.8 6.138 7.556C6.138 8.222 5.706 8.474 4.392 8.834L3.384 9.104C1.836 9.5 0.846 10.292 0.846 11.876C0.846 13.334 1.908 14.144 3.546 14.144ZM3.852 12.848C2.952 12.848 2.412 12.416 2.412 11.678C2.412 11.012 2.79 10.544 3.6 10.292L4.518 10.004C5.58 9.68 6.03 9.554 6.228 9.266V10.184C6.228 10.85 6.138 11.372 5.922 11.714C5.49 12.434 4.752 12.848 3.852 12.848ZM11.7385 6.278H11.6665C11.6665 6.188 11.6845 6.026 11.6845 5.45V5H10.1545V14H11.7205V8.888C11.7205 7.232 12.4585 6.206 14.0245 6.206C15.6265 6.206 16.2205 7.142 16.2205 8.816V14H17.7865V8.51C17.7865 6.296 16.7245 4.838 14.4925 4.838C13.2685 4.838 12.3145 5.252 11.7385 6.278ZM19.0589 14H20.8409L23.2169 10.562L25.6109 14H27.4109L24.1529 9.374L27.2489 5H25.4849L23.2529 8.258L21.0389 5H19.2209L22.3169 9.374L19.0589 14ZM29.6857 3.164C30.3697 3.164 30.8737 2.678 30.8737 1.994C30.8737 1.292 30.3877 0.788 29.6857 0.788C28.9837 0.788 28.4797 1.292 28.4797 1.994C28.4797 2.678 28.9837 3.164 29.6857 3.164ZM28.8937 14H30.4597V5H28.8937V14ZM37.0197 14.144C38.8737 14.144 40.0797 13.298 40.7817 11.786L40.8357 11.66L39.4497 11.156L39.4137 11.3C38.9637 12.2 38.2617 12.812 37.0017 12.812C35.0757 12.812 34.2477 11.57 34.1217 10.004H40.9977V9.356C40.9977 6.368 39.4137 4.856 36.8757 4.856C34.2837 4.856 32.6097 6.818 32.6097 9.554C32.6097 12.218 33.9777 14.144 37.0197 14.144ZM36.8757 6.17C38.3877 6.17 39.3237 6.908 39.4677 8.762H34.1397C34.3737 7.214 35.3277 6.17 36.8757 6.17ZM47.9417 14V12.65H46.6097C45.8717 12.65 45.6557 12.452 45.6557 11.498V6.332H47.9057V5H45.6557V1.4H44.1797V4.46C44.1797 4.856 44.2157 4.964 44.2157 5.018C44.2157 5.036 44.1977 5.054 44.1797 5.054C44.1437 5.054 44.0537 5 43.6397 5H42.2177V6.332H44.1617V11.606C44.1617 13.316 44.6477 14 46.0157 14H47.9417ZM49.9665 17.96H51.0465C52.6845 17.96 53.2425 17.546 54.0165 15.53L58.1385 5H56.5005L53.7465 12.29L50.8305 5H49.1565L52.9545 14.288L52.6845 15.008C52.1805 16.322 51.8205 16.574 50.8845 16.574H49.9665V17.96Z"
                  fill="#662D8C"
                />
              </svg>`
            )
            return (
              <BulletGroup key={bullet.link}>
                <BulletIcon src={BulletBlob} alt="Bullet icon" />
                <OutboundLink href={bullet.link} target="_blank" rel="noopener noreferrer">
                  <StudyTitle dangerouslySetInnerHTML={{ __html: displayTitle }} />
                </OutboundLink>
                <StudyCitation>{bullet.citation}</StudyCitation>
              </BulletGroup>
            )
          })}
        </BulletGrid>
      </TabWrapper>
    </TabGroup>
  )
}

export default Research
